import React, { useReducer, useMemo } from "react";
import { thumbnail7n } from "@/utils";
import { ImageSort, UserImage, Color } from "./mini-me";
import { getKeyPoint } from ".";
import styles from "./style.less";

interface IChoosePreviewOptionProps {
  bodyUrl: string;
  /** 列表图片数据 */
  option: ImageSort;
  /** 底图宽度 */
  bottomImageWidth: number;
  /** 底图高度 */
  bottomImageHeight: number;
  /** 头像 */
  avatar: UserImage;
  /** 已选择的肤色 */
  selectedColor: Color;
  /** 指定宽度 */
  avatarWidth: string | number;
  /** 指定高度 */
  avatarHeight: string | number;
  /** 指定X轴偏移量 */
  avatarLeft: string | number;
  /** 指定Y轴偏移量 */
  avatarTop: string | number;
  /** 指定缩放比例 */
  avatarScale: number;
  /** 强制更新函数 */
  forceUpdate: React.DispatchWithoutAction;
}

const ChoosePreviewOption = ({
  bodyUrl,
  option,
  bottomImageWidth,
  bottomImageHeight,
  avatar,
  selectedColor,
  avatarWidth,
  avatarHeight,
  avatarLeft,
  avatarTop,
  avatarScale,
  forceUpdate,
}: IChoosePreviewOptionProps) => {
  /** 加载完毕回调 */
  const handleLoaded = (image?: string) => {
    if (image) {
      option.loading[image] = false;
    } else {
      for (let i in option.loading) {
        option.loading[i] = false;
      }
    }
    forceUpdate();
  };

  return (
    <div className={styles.imageWrapper}>
      <img
        className={styles.body}
        src={thumbnail7n(bodyUrl, parseInt(`${bottomImageHeight * 2}`))}
        onLoad={() => handleLoaded("body")}
      />
      <div
        className={styles.avatar}
        style={{
          left: avatarLeft,
          top: avatarTop,
          width: avatarWidth,
          height: avatarHeight,
          backgroundImage: `url(${avatar.image})`,
        }}
      />
      {option.origin.annex?.map((annexData) => {
        // 如果人脸识别成功就适配配件
        if (avatar.faceRecognizeData && avatarScale && option.origin.face) {
          // 寻找对应肤色的地址
          const imageUrl = annexData.images.find(
            (item) => item.color === selectedColor.name
          )!.url;
          const url = thumbnail7n(imageUrl, 200);
          // 拿到压缩后图片的宽高
          // 配件图片缩放比
          let scale = 200 / annexData.width;
          if (annexData.height > annexData.width) {
            scale = 200 / annexData.height;
          }
          const annexDataWidth = annexData.width * scale;
          const annexDataHeight = annexData.height * scale;
          // 获取人脸识别数据
          const { face } = option.origin;
          const { faceRect } = avatar.faceRecognizeData;
          const { leftPoint, rightPoint, feature } = annexData.faceFit;
          // 实际图片的脸区域的宽高
          const avatarFaceWidth = faceRect.width * avatarScale;
          // 脸部安全区大小
          const faceFitWidth =
            (face.width / option.origin.width) * bottomImageWidth;
          // 脸部安全区坐标
          const faceFitLeft =
            (face.left / option.origin.width) * bottomImageWidth;
          // 拿到关键点坐标
          const keyPoint = getKeyPoint(avatar, feature);

          let annexTop;
          const annexScale =
            avatarFaceWidth / ((rightPoint.left - leftPoint.left) * scale);
          const tempAvatarHeight =
            typeof avatarHeight === "string"
              ? (parseFloat(avatarHeight) / 100) * bottomImageHeight
              : avatarHeight;
          const tempAvatarTop =
            typeof avatarTop === "string"
              ? (parseFloat(avatarTop) / 100) * bottomImageHeight
              : avatarTop;
          if (feature === "head") {
            // 100 * avatarScale 非常不稳定，识别的头尺寸小会导致帽子位置过高
            annexTop =
              tempAvatarTop -
              tempAvatarHeight +
              keyPoint.y * avatarScale -
              100 * avatarScale -
              leftPoint.top * scale * annexScale;
          } else {
            annexTop =
              tempAvatarTop -
              tempAvatarHeight +
              keyPoint.y * avatarScale -
              leftPoint.top * scale * annexScale;
          }

          const faceLeft = faceFitLeft + (faceFitWidth - avatarFaceWidth) / 2;
          const annexLeft = faceLeft - leftPoint.left * scale * annexScale;

          return (
            <img
              key={annexData.id}
              className={styles.annex}
              src={url}
              onLoad={() => handleLoaded(`annex-${annexData.id}`)}
              style={{
                left: annexLeft,
                top: annexTop,
                width: annexDataWidth * annexScale,
                height: annexDataHeight * annexScale,
              }}
            />
          );
        } else {
          const left =
            (annexData.left / option.origin.width) * bottomImageWidth;
          const top =
            (annexData.top / option.origin.height) *
            ((option.origin.height / option.origin.width) * bottomImageWidth);
          return (
            <img
              key={annexData.id}
              className={styles.annex}
              src={thumbnail7n(
                annexData.images[0].url,
                Math.round(
                  (bottomImageWidth / option.origin.width) * annexData.width
                )
              )}
              onLoad={() => {
                delete option.loading[`annex-${annexData.id}`];
                forceUpdate();
              }}
              style={{
                left: left,
                top: top,
              }}
            />
          );
        }
      })}
    </div>
  );
};

export default ChoosePreviewOption;
